import React from 'react';
import moment from 'moment';
import set from 'lodash/set';
import last from 'lodash/last';
import round from 'lodash/round';
import GeneralChart from '@/components/job-title/GeneralChart';
import { CusEcharts } from '@/components/AsyncComponent';
import type { ECOption } from '@/components/CusEcharts';
import HideChart from '@/components/HideChart';
import DataInterpretationSEO from '@/components/DataInterpretationSEO';
import { JobTitleContext } from '@/context/create-context';
import { TAB_JOB_FUTURE } from '@/constants/job-title';

type Props = {
  footer?: React.ReactNode;
};

const JobFutureCard = ({ footer }: Props) => {
  const {
    jobTitle: {
      name,
      job_open_details: {
        new_job_details: { by_month: newJobDetailsByMonth = [] },
      },
      city_name: cityName = '',
    },
    jobTitleSummary: {
      new_job_details: { by_month: newJobDetailsBySummary = [] },
    },
    displayName,
  } = React.useContext(JobTitleContext);
  const lineChartRef = React.useRef(null);

  const seoValue = React.useMemo(() => {
    const { name, value, rank } =
      newJobDetailsByMonth[newJobDetailsByMonth.length - 1];
    return { name, value, rank, month: name.split('-')[1] };
  }, [newJobDetailsByMonth]);

  const lineConfig = React.useMemo<{ config: ECOption; data: any[] }>(() => {
    const reGetGrowthRate = (value: any[]) => {
      const growthRateValue = set(
        value.map((item) => item?.growth_rate as number),
        '[0]',
        0,
      );
      return growthRateValue.reduce((pre: number[], cur) => {
        const lastValue = last(pre) ?? 0;
        const curValue = cur ?? 0;
        const sum = round(lastValue + curValue, 3);
        pre.push(sum);
        return pre;
      }, []);
    };
    const jobDetailsByMonth = newJobDetailsByMonth.slice(-4);
    const JobDetailsBySummary = jobDetailsByMonth.map((item) =>
      newJobDetailsBySummary.find((sItem) => sItem?.name === item?.name),
    );
    const jobDetailsByMonthGrowthRate = reGetGrowthRate(jobDetailsByMonth);
    const JobDetailsBySummaryGrowthRate = reGetGrowthRate(JobDetailsBySummary);
    const jobDetailsByMonthData = jobDetailsByMonth.map((item, index) => ({
      name,
      month: `${parseInt(item?.name.split('-')[1])}月`,
      value: jobDetailsByMonthGrowthRate[index],
    }));
    const jobDetailsBySummaryData = jobDetailsByMonth.map((item, index) => ({
      name: '全部职位',
      month: `${parseInt(item?.name.split('-')[1])}月`,
      value: JobDetailsBySummaryGrowthRate[index],
    }));
    const totalGrowthRate = jobDetailsByMonthData
      .concat(jobDetailsBySummaryData)
      .map((item) => item?.value ?? 0);
    const max = Math.max(...totalGrowthRate);
    const min = Math.min(...totalGrowthRate);

    return {
      config: {
        grid: {
          show: false,
          top: '20%',
          bottom: '20%',
          left: '15%',
          right: '15%',
        },
        xAxis: {
          type: 'category',
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: 'rgba(0,0,0,0.85)', padding: 10 },
          data: jobDetailsBySummaryData.map((item) => item.month),
        },
        yAxis: {
          type: 'value',
          show: false,
          min: min - 0.06,
          max: max + 0.06,
        },
        legend: {
          show: true,
          type: 'plain',
          right: 0,
          itemHeight: 12,
          lineStyle: { width: 0 },
          selectedMode: false,
          formatter: (name) =>
            name.length > 25 ? name.substring(0, 25) + '...' : name,
        },
        series: [
          {
            name,
            type: 'line',
            zlevel: 1,
            data: jobDetailsByMonthData,
            smooth: true,
            cursor: 'default',
            emphasis: { disabled: true },
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#a6cee3',
            },
          },
          {
            name: '全部职位',
            type: 'line',
            zlevel: 1,
            data: jobDetailsBySummaryData,
            smooth: true,
            cursor: 'default',
            emphasis: { disabled: true },
            symbol: 'circle',
            symbolSize: 6,
            itemStyle: {
              color: '#008489',
            },
            markLine: {
              silent: true,
              symbol: 'none',
              data: [
                {
                  yAxis: min,
                },
                {
                  yAxis: max,
                },
              ],
              label: {
                position: 'start',
                fontSize: 13,
                fontWeight: 600,
                distance: [-10, 0],
                formatter: ({ value }) => `${Math.floor(Number(value) * 100)}%`,
              },
              lineStyle: {
                color: '#f0f0f0',
                width: 2,
                type: 'solid',
              },
            },
          },
        ],
      },
      data: jobDetailsBySummaryData,
    };
  }, [name, newJobDetailsByMonth, newJobDetailsBySummary]);

  return (
    <GeneralChart
      type="custom"
      title={`${cityName}${displayName}就业前景怎么样？`}
      footer={footer}
    >
      <div className="px-5 py-4 tablet:p-0 tablet:grid tablet:grid-cols-[42.5%_2px_57.5%]">
        <div className="tablet:py-6 tablet:px-12">
          <p className="font-medium text-base">
            <span className="text-dark-cyan">{`${seoValue.month}月`}</span>
            新增职位
          </p>
          <p className="font-bold mt-2 text-2.75xl tablet:text-4xl">
            {seoValue.value}
          </p>
          <div className="mt-1 text-base leading-6.5 tablet:mt-4 tablet:text-xs tablet:leading-5">
            <span>{`排名第${seoValue.rank}`}</span>
            <br />
            <span>预计该职位在未来将处于强需求状态</span>
            <DataInterpretationSEO
              className="mt-3 text-opacity-65 tablet:mt-5"
              dataEleGroup={`${TAB_JOB_FUTURE}_summary`}
            >
              {`${cityName}${displayName}岗位的就业前景怎么样？${moment(
                seoValue.name,
              ).format('yyyy年M月')}${cityName}${displayName}新增${
                seoValue.value
              }个岗位，在全网公开岗位中新增岗位数排名第${
                seoValue.rank
              }名，${cityName}${displayName}这一岗位未来的市场需求仍较大。数据由各大平台公开数据统计分析而来，请客观对待，仅供参考。`}
            </DataInterpretationSEO>
          </div>
        </div>
        <div className="w-full h-px bg-white-smoke mt-6 tablet:h-full tablet:w-0.5 tablet:m-0" />
        <div className="mt-7 tablet:py-6 tablet:px-12 tablet:m-0">
          <HideChart
            isHide={lineConfig.data.length <= 1}
            hideTitle="职位增长比率"
          >
            <>
              <span className="font-medium text-sm">职位增长比率</span>
              <div className="h-52 [&_canvas]:cursor-default">
                <CusEcharts
                  chartRef={lineChartRef}
                  config={lineConfig.config}
                />
              </div>
            </>
          </HideChart>
        </div>
      </div>
    </GeneralChart>
  );
};

export default JobFutureCard;
